<script setup lang="ts">
import {
  ArrowDown,
  ArrowUp,
  CirclePlus,
  Delete,
  Hide,
} from "@element-plus/icons-vue";
import { ElMessage } from "element-plus";
import { computed, onMounted, ref } from "vue";
import { FormElementType, WorkflowDefinition, WorkflowMetaCode } from "~/api";
import { apiClient } from "~/utils/apiClient";
import { emptyGuid } from "~/utils/constants";
import { showError } from "~/utils/handleAPIError";

type listItemType = WorkflowDefinition;

const props = defineProps<{
  workflowDefinition: listItemType;
  readOnly?: boolean;
}>();

const emit = defineEmits(["updated"]);

const selectedQuestions = ref<any[]>([]);
const availableQuestions = ref<any[]>([]);

const standardColumns = [
  {
    guid: emptyGuid,
    label: "Name",
    metaCode: WorkflowMetaCode.NAME,
    disabled: false,
  },
  {
    guid: emptyGuid,
    label: "For",
    metaCode: WorkflowMetaCode.FOR,
    disabled: true,
  },
  {
    guid: emptyGuid,
    label: "Status",
    metaCode: WorkflowMetaCode.STATUS,
    disabled: true,
  },
  {
    guid: emptyGuid,
    label: "Created At",
    metaCode: WorkflowMetaCode.UPDATED_AT,
    disabled: true,
  },
];

const handleAddRow = (row: any) => {
  selectedQuestions.value.push({ ...row });
  availableQuestions.value = availableQuestions.value.filter(
    (item) => item !== row,
  );
};

const handleDeleteRow = (row: any) => {
  if (row.metaCode) {
    row.disabled = !row.disabled;
  } else {
    availableQuestions.value.push({ ...row });
    selectedQuestions.value = selectedQuestions.value.filter(
      (item) => item !== row,
    );
  }
};

const rowIndexReordered = ref<number | undefined>(undefined);

const handleChangeOrder = (option: any, change: number) => {
  if (option.metaCode === WorkflowMetaCode.NAME) {
    return; // name column must be first and cannot be reordered
  }
  const oldIndex = selectedQuestions.value.indexOf(option);
  if (oldIndex === -1) {
    showError(
      "Developer Error: tried to reorder non-existent selected question code",
    );
  }
  let newIndex = oldIndex + change;

  const nameIndex = selectedQuestions.value.findIndex(
    (q) => q.metaCode === WorkflowMetaCode.NAME,
  );
  const minIndex = nameIndex >= 0 ? nameIndex + 1 : 0;

  if (newIndex < minIndex) {
    newIndex = minIndex;
  } else if (newIndex >= selectedQuestions.value.length) {
    newIndex = selectedQuestions.value.length - 1;
  }
  selectedQuestions.value.splice(oldIndex, 1);
  selectedQuestions.value.splice(newIndex, 0, option);

  setTimeout(() => (rowIndexReordered.value = undefined), 1000);
};

const updateWorkFlowDefinitionListing = async () => {
  const updatedWorkflowDefinition = {
    ...props.workflowDefinition,
    questionsToShow: selectedQuestions.value
      .filter((sq) => !sq.disabled)
      .map((sq) => ({
        guid: sq.guid,
        questionCode: sq.questionCode,
        label: sq.label,
        form: sq.form,
        metaCode: sq.metaCode,
        type: sq.type,
        width: sq.width,
        isSecurityQuestion: sq.isSecurityQuestion,
      })),
  };
  try {
    await apiClient.workflowDefinition
      .updateWorkflowDefinitionListing(
        props.workflowDefinition.guid,
        updatedWorkflowDefinition,
      )
      .then(() => {
        ElMessage.success("Saved");
        emit("updated", updatedWorkflowDefinition);
      })
      .catch(showError);
  } catch (error) {
    showError(error);
  }
};

// Only include form elements that can be used as questions
const excludedFormElements = [
  FormElementType.ANSWER_REVIEW,
  FormElementType.CHANGE_STATUS_BUTTON,
  FormElementType.SECTION,
  FormElementType.HEADING,
  FormElementType.FORM_TEXT,
  FormElementType.LINKED_ANSWER,
];

const isSecurityFilterColumn = computed(() => {
  return selectedQuestions.value.some(
    (row) =>
      row.type === FormElementType.ENTITY ||
      row.type === FormElementType.SECURITY_GROUP,
  );
});

onMounted(async () => {
  const allQuestions =
    props.workflowDefinition.formDefinitions?.flatMap((fd) =>
      fd.components
        .filter(
          (component) =>
            !excludedFormElements.includes(component.type) &&
            !component.isArchived,
        )
        .map((component) => ({
          ...component,
          form: fd.name,
        })),
    ) ?? [];

  selectedQuestions.value = props.workflowDefinition.questionsToShow ?? [];

  const hiddenColumns = standardColumns
    .filter(
      (column) =>
        !selectedQuestions.value.some((sq) => sq.metaCode === column.metaCode),
    )
    .map((column) => ({ ...column, disabled: true }));
  selectedQuestions.value.push(...hiddenColumns); // ensure that hidden standard columns are still listed in the table

  const nameIndex = selectedQuestions.value.findIndex(
    (question) => question.metaCode === WorkflowMetaCode.NAME,
  );

  if (nameIndex > 0) {
    // name column should always be first
    const nameQuestion = selectedQuestions.value.splice(nameIndex, 1)[0];
    selectedQuestions.value.unshift(nameQuestion);
  }

  availableQuestions.value = allQuestions.filter(
    (question) =>
      !selectedQuestions.value.some(
        (selected) => selected.guid === question.guid,
      ),
  );
});
</script>

<template>
  <div>
    <el-button
      v-if="selectedQuestions.length > 0 && !props.readOnly"
      type="success"
      @click="updateWorkFlowDefinitionListing()"
      >Save</el-button
    >
  </div>
  <br />
  <br />

  <div class="WorkflowListing">
    <h3>Columns To Show</h3>
    <div class="WorkflowListingSection">
      <el-table
        :data="selectedQuestions"
        style="width: 100%"
        :row-class-name="({ row }) => (row.disabled ? 'disabled-row' : '')"
      >
        <el-table-column width="60">
          <template #default="{ row }">
            <div v-if="!props.readOnly" class="order-arrows">
              <el-button
                v-if="row.metaCode !== WorkflowMetaCode.NAME"
                :icon="ArrowUp"
                size="small"
                :disabled="
                  row.disabled || row.metaCode === WorkflowMetaCode.NAME
                "
                @click="() => handleChangeOrder(row, -1)"
              />
              <el-button
                v-if="row.metaCode !== WorkflowMetaCode.NAME"
                :icon="ArrowDown"
                size="small"
                :disabled="
                  row.disabled || row.metaCode === WorkflowMetaCode.NAME
                "
                @click="() => handleChangeOrder(row, 1)"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Content " width="300">
          <template #default="{ row }">
            <span v-if="row.questionCode">
              {{ row.form }} : {{ row.questionCode }}<br />
            </span>
            <span v-if="row.metaCode">
              Workflow : {{ row.metaCode }}<br />
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Label" width="250">
          <template #default="{ row }">
            <el-input
              v-model="row.label"
              :disabled="props.readOnly || row.disabled"
            >
            </el-input>
          </template>
        </el-table-column>
        <el-table-column label="Width" width="140">
          <template #default="{ row }">
            <el-input
              v-model="row.width"
              :disabled="props.readOnly || row.disabled"
            >
            </el-input>
          </template>
        </el-table-column>
        <el-table-column
          v-if="isSecurityFilterColumn"
          label="Mark as Security Filter"
          align="center"
          width="200"
        >
          <template #default="{ row }">
            <el-checkbox
              v-if="
                row.type === FormElementType.ENTITY ||
                row.type === FormElementType.SECURITY_GROUP
              "
              v-model="row.isSecurityQuestion"
              :disabled="props.readOnly || row.disabled"
            >
            </el-checkbox>
          </template>
        </el-table-column>
        <el-table-column label="Action" align="left">
          <template #default="{ row }">
            <el-button
              v-if="row.questionCode"
              :disabled="props.readOnly"
              :icon="Delete"
              size="small"
              @click="() => handleDeleteRow(row)"
            />
            <el-button
              v-if="row.metaCode"
              :disabled="props.readOnly"
              :icon="row.disabled ? CirclePlus : Hide"
              size="small"
              @click="() => handleDeleteRow(row)"
            />
          </template>
        </el-table-column>
      </el-table>
    </div>
    <h3>Available Questions</h3>
    <div class="WorkflowListingSection">
      <el-table :data="availableQuestions" style="width: 100%">
        <el-table-column width="60">
          <template #default="{ row }">
            <div class="order-arrows">
              <el-button
                v-if="!props.readOnly"
                :icon="CirclePlus"
                size="default"
                @click="() => handleAddRow(row)"
              />
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="form" label="Form" />

        <el-table-column label="Question Code">
          <template #default="{ row }"> {{ row.questionCode }}<br /> </template>
        </el-table-column>
      </el-table>

      <br />
    </div>
  </div>
</template>

<style lang="less" scoped>
.WorkflowListing {
  overflow-y: auto;
  height: 100%;
}
.WorkflowListingSection {
  .el-form {
    width: 70%;
    margin: 0 auto 20px;
    padding: 5px 10px 2px;
    border: 2px solid #ebeef5;
    display: grid;
    grid-template-columns: 2fr 2fr auto;
    align-items: center;
    justify-content: space-between;
    grid-gap: 1em;
  }
  .create-button {
    .el-form-item__content {
      justify-content: end;
    }
  }
}

:deep(.disabled-row) {
  background-color: #f5f7fa;
  opacity: 0.6;

  td {
    color: #909399;
  }
}
</style>
